import { formatCurrencyFromCents } from "./financials";
import type { ReferralIncentive } from "./referral-links-api";

/**
 * Which promise a referral discovery surface is making. `cash`/`gift_card`
 * come from the live admin config; `waitlist` is the always-on D2 bump. This
 * is also the allowlisted `incentive` analytics property.
 */
export type ReferralSurfaceIncentive = "cash" | "gift_card" | "waitlist";

export interface ReferralSurfaceCopy {
  headline: string;
  body: string;
  incentive: ReferralSurfaceIncentive;
}

export interface ReferralSurfaceCopyInput {
  /** Validated enabled incentive from GET /api/referrals/me, or null. */
  incentive: ReferralIncentive | null;
  /** Target-area label when the buyer is queued for a pre-launch metro, else null. */
  waitlistArea: string | null;
  /**
   * SILL-504 `marketplace_go_live_enabled`. With the flag OFF nobody can
   * publish or connect, so both monetary earn milestones are unreachable —
   * a "$25 when a friend publishes" line would be a promise with no path to
   * fulfilment. The backend already earns nothing in that state; this keeps
   * the COPY honest too, so an admin arming the config row before the flip
   * cannot over-promise. Required (not defaulted) so no caller forgets it.
   */
  marketplaceLive: boolean;
}

function milestoneClause(milestone: ReferralIncentive["milestone"]): string {
  return milestone === "connection"
    ? "when a friend connects with an agent"
    : "when a friend publishes";
}

/**
 * The ONE resolver of what a referral discovery surface says. Every surface
 * (dashboard banner, post-publish prompt, waitlist card sheet) renders from
 * this so the promise cannot drift between them.
 *
 * Precedence: a live monetary incentive (only while the marketplace flag is
 * on — see `marketplaceLive`) → else the waitlist bump when the buyer is
 * actually queued → else NOTHING (null). A loud surface with no
 * payoff trains users to ignore it, so "invite a friend" with nothing behind
 * it is deliberately not a state. A `$0` promise is treated as
 * misconfiguration and falls through to the waitlist rule — the amount is
 * read from the backend and formatted here, never derived.
 */
export function referralSurfaceCopy({
  incentive,
  waitlistArea,
  marketplaceLive,
}: ReferralSurfaceCopyInput): ReferralSurfaceCopy | null {
  if (marketplaceLive && incentive && incentive.value_cents > 0) {
    const amount = formatCurrencyFromCents(incentive.value_cents);
    const clause = milestoneClause(incentive.milestone);
    if (incentive.kind === "gift_card") {
      return {
        headline: `Get a ${amount} gift card ${clause}`,
        body: "Share your link with a friend who's house hunting. When they finish the milestone, the gift card is yours.",
        incentive: "gift_card",
      };
    }
    return {
      headline: `Get ${amount} ${clause}`,
      body: "Share your link with a friend who's house hunting. When they finish the milestone, the reward is yours.",
      incentive: "cash",
    };
  }
  if (waitlistArea) {
    return {
      headline: `Move up the ${waitlistArea} waitlist`,
      body: "Each friend who joins through your link and publishes moves you up the line — and brings Sill to your area sooner.",
      incentive: "waitlist",
    };
  }
  return null;
}
